import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Bookmark, ChevronDown, Filter, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import EmploymentTermsPanel from "../../components/admin/EmploymentTermsPanel";
import PageHeader from "../../components/PageHeader";
import { ActiveBadge } from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const EMPTY = {
  name: "",
  company_id: "",
  engagement_type: "EMPLOYEE",
  cost_classification: "CONSULTANT",
  start_date: "",
  end_date: "",
  employment_percentage: "100",
};

const ENGAGEMENT_LABELS = { EMPLOYEE: "Employee", SUBCONTRACTOR: "Subcontractor" };
const CLASSIFICATION_LABELS = { CONSULTANT: "Consultant", OVERHEAD: "Overhead" };
const STATUS_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const EMPTY_FILTERS = { companies: [], engagements: [], classifications: [], status: "ALL" };
const SAVED_KEY = "elva-people-saved-filters";

const loadSavedFilters = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(SAVED_KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function PeoplePage() {
  const { isAdmin } = useAuth();
  const [people, setPeople] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [termsFor, setTermsFor] = useState(null);
  const [editHasTerms, setEditHasTerms] = useState(false);

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  // Search + filters (presentation only — backend company scoping stays authoritative)
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [savedFilters, setSavedFilters] = useState(loadSavedFilters);
  const [savedMenuOpen, setSavedMenuOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const activeFilterCount =
    filters.companies.length +
    filters.engagements.length +
    filters.classifications.length +
    (filters.status !== "ALL" ? 1 : 0);

  const toggleIn = (key, value) =>
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter((v) => v !== value) : [...prev[key], value],
    }));

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearch("");
  };

  const persistSaved = (list) => {
    setSavedFilters(list);
    localStorage.setItem(SAVED_KEY, JSON.stringify(list));
  };

  const saveCurrentFilter = () => {
    const name = saveName.trim();
    if (!name) return;
    if (savedFilters.some((f) => f.name.toLowerCase() === name.toLowerCase())) {
      toast.error("A saved filter with that name already exists");
      return;
    }
    persistSaved([...savedFilters, { name, filters }]);
    toast.success(`Filter "${name}" saved`);
    setSaveDialogOpen(false);
    setSaveName("");
  };

  const applySavedFilter = (f) => {
    setFilters({ ...EMPTY_FILTERS, ...f.filters });
    toast.success(`Filter "${f.name}" applied`);
  };

  const renameSavedFilter = () => {
    const name = renameValue.trim();
    if (!name) return;
    if (savedFilters.some((f) => f.name.toLowerCase() === name.toLowerCase() && f.name !== renameTarget)) {
      toast.error("A saved filter with that name already exists");
      return;
    }
    persistSaved(savedFilters.map((f) => (f.name === renameTarget ? { ...f, name } : f)));
    toast.success("Filter renamed");
    setRenameTarget(null);
  };

  const deleteSavedFilter = (name) => {
    persistSaved(savedFilters.filter((f) => f.name !== name));
    toast.success(`Filter "${name}" deleted`);
  };

  // Controlled initial-engagement correction (admin) — data correction, not a
  // real engagement transition. Eligibility is enforced by the backend.
  const [correctionFor, setCorrectionFor] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({
    cost_classification: "CONSULTANT",
    employment_percentage: "100",
    fte_monthly_salary: "",
    pension_amount: "",
    comment: "",
  });
  const [correctionBusy, setCorrectionBusy] = useState(false);

  const openCorrection = async (p) => {
    if (!p.start_date) {
      toast.error("Initial engagement correction requires a start date on the person");
      return;
    }
    if (p.engagement_type === "EMPLOYEE") {
      try {
        const res = await api.get(`/people/${p.id}/employment-terms`);
        const eligible = res.data.length <= 1 && (res.data.length === 0 || res.data[0].effective_from === p.start_date);
        if (!eligible) {
          toast.error("Real engagement history exists — use the effective-dated workflow instead");
          return;
        }
      } catch (e) {
        toast.error(apiError(e));
        return;
      }
    }
    setCorrectionForm({
      cost_classification: "CONSULTANT",
      employment_percentage: "100",
      fte_monthly_salary: "",
      pension_amount: "",
      comment: "",
    });
    setCorrectionFor(p);
  };

  const submitCorrection = async () => {
    const toEmployee = correctionFor.engagement_type === "SUBCONTRACTOR";
    const payload = {
      target_engagement_type: toEmployee ? "EMPLOYEE" : "SUBCONTRACTOR",
      comment: correctionForm.comment || null,
    };
    if (toEmployee) {
      payload.cost_classification = correctionForm.cost_classification;
      payload.employment_percentage = Number(correctionForm.employment_percentage);
      payload.fte_monthly_salary = Number(correctionForm.fte_monthly_salary);
      payload.pension_amount = Number(correctionForm.pension_amount);
    }
    setCorrectionBusy(true);
    try {
      await api.post(`/people/${correctionFor.id}/correct-initial-engagement`, payload);
      toast.success("Initial engagement corrected");
      setCorrectionFor(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCorrectionBusy(false);
    }
  };

  // UI-only column sorting — no API/model change; all records are loaded client-side.
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const toggleSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const sortValue = (p, key) => {
    switch (key) {
      case "name": return p.name?.toLowerCase() ?? "";
      case "company": return companyName(p.company_id).toLowerCase();
      case "engagement": return p.engagement_type === "EMPLOYEE" ? "employee" : "subcontractor";
      case "classification": return p.cost_classification === "CONSULTANT" ? "consultant" : "overhead";
      case "start_date": return p.start_date || null;
      case "end_date": return p.end_date || null;
      default: return "";
    }
  };

  // Pipeline: source people → filters (AND across groups, OR within) → search → sort → render
  const visiblePeople = useMemo(() => {
    let list = people;
    if (filters.companies.length > 0) list = list.filter((p) => filters.companies.includes(p.company_id));
    if (filters.engagements.length > 0) list = list.filter((p) => filters.engagements.includes(p.engagement_type));
    if (filters.classifications.length > 0) list = list.filter((p) => filters.classifications.includes(p.cost_classification));
    if (filters.status === "ACTIVE") list = list.filter((p) => p.active);
    if (filters.status === "INACTIVE") list = list.filter((p) => !p.active);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        [p.name, companyName(p.company_id), p.email]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q))
      );
    }
    if (!sort.key) return list; // preserve default load order until a header is clicked
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      // ongoing/null dates: last when ascending, first when descending — never artificial dates
      if (va == null && vb == null) return 0;
      if (va == null) return sort.dir === "asc" ? 1 : -1;
      if (vb == null) return sort.dir === "asc" ? -1 : 1;
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [people, filters, search, sort, companies]); // eslint-disable-line react-hooks/exhaustive-deps

  const SortHeader = ({ label, sortKey, testId }) => (
    <th className="py-3 px-4 text-left">
      <button
        type="button"
        onClick={() => toggleSort(sortKey)}
        className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
        data-testid={testId}
      >
        {label}
        {sort.key === sortKey ? (
          sort.dir === "asc" ? <ArrowUp className="h-3 w-3 text-[#0052CC]" /> : <ArrowDown className="h-3 w-3 text-[#0052CC]" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-slate-300" />
        )}
      </button>
    </th>
  );

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/people"), api.get("/companies")]);
    setPeople(p.data);
    setCompanies(c.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setEditHasTerms(false);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = async (person) => {
    setEditing(person);
    let pct = "100";
    let hasTerms = false;
    if (person.engagement_type === "EMPLOYEE") {
      try {
        const res = await api.get(`/people/${person.id}/employment-terms`);
        const terms = res.data;
        hasTerms = terms.length > 0;
        const today = new Date().toISOString().slice(0, 10);
        const applicable = terms
          .filter((t) => t.effective_from <= today && (!t.effective_to || t.effective_to >= today))
          .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
        const latest = [...terms].sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
        const current = applicable || latest;
        if (current) pct = String(current.employment_percentage);
      } catch (e) {
        toast.error(apiError(e));
      }
    }
    setEditHasTerms(hasTerms);
    setForm({
      name: person.name,
      company_id: person.company_id,
      engagement_type: person.engagement_type,
      cost_classification: person.cost_classification,
      start_date: person.start_date || "",
      end_date: person.end_date || "",
      employment_percentage: pct,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      cost_classification:
        form.engagement_type === "SUBCONTRACTOR" ? "CONSULTANT" : form.cost_classification,
    };
    if (form.engagement_type === "EMPLOYEE") {
      // Only send when creating the initial terms (new person, or existing employee without terms).
      if (!editing || !editHasTerms) {
        payload.employment_percentage = Number(form.employment_percentage);
      } else {
        delete payload.employment_percentage;
      }
    } else {
      delete payload.employment_percentage;
    }
    try {
      if (editing) {
        await api.patch(`/people/${editing.id}`, payload);
        toast.success("Person updated");
      } else {
        await api.post("/people", payload);
        toast.success("Person added");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (person) => {
    try {
      await api.patch(`/people/${person.id}`, { active: !person.active });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="people-page">
      <PageHeader
        title="People"
        subtitle="Employees and subcontractors (master data foundation)"
        actions={
          isAdmin && (
            <Button onClick={openCreate} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="new-person-button">
              <Plus className="h-4 w-4 mr-1.5" /> New person
            </Button>
          )
        }
      />
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="people-toolbar">
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, company or email…"
            className="pl-9 pr-8 w-72"
            data-testid="people-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              data-testid="people-search-clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="people-filter-button">
              <Filter className="h-4 w-4 mr-1.5" />
              Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72" data-testid="people-filter-panel">
            <div className="space-y-4">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Company</div>
                <div className="space-y-1.5 max-h-44 overflow-y-auto">
                  {companies.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <Checkbox
                        checked={filters.companies.includes(c.id)}
                        onCheckedChange={() => toggleIn("companies", c.id)}
                        data-testid={`filter-company-${c.id}`}
                      />
                      {c.name}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Engagement</div>
                <div className="space-y-1.5">
                  {Object.entries(ENGAGEMENT_LABELS).map(([value, label]) => (
                    <label key={value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <Checkbox
                        checked={filters.engagements.includes(value)}
                        onCheckedChange={() => toggleIn("engagements", value)}
                        data-testid={`filter-engagement-${value}`}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Classification</div>
                <div className="space-y-1.5">
                  {Object.entries(CLASSIFICATION_LABELS).map(([value, label]) => (
                    <label key={value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <Checkbox
                        checked={filters.classifications.includes(value)}
                        onCheckedChange={() => toggleIn("classifications", value)}
                        data-testid={`filter-classification-${value}`}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Status</div>
                <div className="flex gap-1.5">
                  {STATUS_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setFilters((prev) => ({ ...prev, status: o.value }))}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        filters.status === o.value
                          ? "bg-[#0052CC] border-[#0052CC] text-white"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                      data-testid={`filter-status-${o.value}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <DropdownMenu open={savedMenuOpen} onOpenChange={setSavedMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="people-saved-filters-button">
              <Bookmark className="h-4 w-4 mr-1.5" />
              Saved filters
              <ChevronDown className="h-3.5 w-3.5 ml-1 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64" data-testid="people-saved-filters-menu">
            <DropdownMenuItem
              disabled={activeFilterCount === 0}
              onClick={() => {
                setSaveName("");
                setSaveDialogOpen(true);
              }}
              data-testid="people-save-filter-button"
            >
              <Plus className="h-4 w-4 mr-2" /> Save current filters…
            </DropdownMenuItem>
            {savedFilters.length > 0 && <DropdownMenuSeparator />}
            {savedFilters.length === 0 && (
              <DropdownMenuItem disabled data-testid="saved-filters-empty">No saved filters yet</DropdownMenuItem>
            )}
            {savedFilters.map((f) => (
              <div key={f.name} className="flex items-center pr-1" data-testid={`saved-filter-${f.name}`}>
                <DropdownMenuItem className="flex-1 cursor-pointer" onClick={() => applySavedFilter(f)} data-testid={`saved-filter-apply-${f.name}`}>
                  {f.name}
                </DropdownMenuItem>
                <button
                  type="button"
                  className="p-1.5 text-slate-400 hover:text-slate-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSavedMenuOpen(false);
                    setRenameTarget(f.name);
                    setRenameValue(f.name);
                  }}
                  data-testid={`saved-filter-rename-${f.name}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="p-1.5 text-slate-400 hover:text-[#B91C1C]"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSavedFilter(f.name);
                  }}
                  data-testid={`saved-filter-delete-${f.name}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {(activeFilterCount > 0 || search) && (
          <Button variant="ghost" onClick={clearFilters} data-testid="people-clear-filters">
            <X className="h-4 w-4 mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {activeFilterCount > 0 && (
        <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="people-filter-chips">
          {filters.companies.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 rounded-full bg-[#EBF3FE] border border-[#CFE3FC] text-[#0047B3] text-xs font-medium px-2.5 py-1" data-testid={`people-chip-company-${id}`}>
              {companyName(id)}
              <button type="button" onClick={() => toggleIn("companies", id)} data-testid={`people-chip-company-${id}-remove`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {filters.engagements.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 rounded-full bg-[#EBF3FE] border border-[#CFE3FC] text-[#0047B3] text-xs font-medium px-2.5 py-1" data-testid={`people-chip-engagement-${v}`}>
              {ENGAGEMENT_LABELS[v]}
              <button type="button" onClick={() => toggleIn("engagements", v)} data-testid={`people-chip-engagement-${v}-remove`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {filters.classifications.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 rounded-full bg-[#EBF3FE] border border-[#CFE3FC] text-[#0047B3] text-xs font-medium px-2.5 py-1" data-testid={`people-chip-classification-${v}`}>
              {CLASSIFICATION_LABELS[v]}
              <button type="button" onClick={() => toggleIn("classifications", v)} data-testid={`people-chip-classification-${v}-remove`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {filters.status !== "ALL" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#EBF3FE] border border-[#CFE3FC] text-[#0047B3] text-xs font-medium px-2.5 py-1" data-testid="people-chip-status">
              {STATUS_OPTIONS.find((o) => o.value === filters.status)?.label}
              <button type="button" onClick={() => setFilters((prev) => ({ ...prev, status: "ALL" }))} data-testid="people-chip-status-remove">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="people-table">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <SortHeader label="Name" sortKey="name" testId="people-sort-name" />
              <SortHeader label="Company" sortKey="company" testId="people-sort-company" />
              <SortHeader label="Engagement" sortKey="engagement" testId="people-sort-engagement" />
              <SortHeader label="Classification" sortKey="classification" testId="people-sort-classification" />
              <SortHeader label="Start Date" sortKey="start_date" testId="people-sort-start-date" />
              <SortHeader label="End Date" sortKey="end_date" testId="people-sort-end-date" />
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
              <th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {visiblePeople.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-accent/40 transition-colors" data-testid={`person-row-${p.id}`}>
                <td className="py-3 px-4 text-sm font-semibold text-slate-800">{p.name}</td>
                <td className="py-3 px-4 text-sm text-slate-700">{companyName(p.company_id)}</td>
                <td className="py-3 px-4 text-sm text-slate-700">{p.engagement_type === "EMPLOYEE" ? "Employee" : "Subcontractor"}</td>
                <td className="py-3 px-4 text-sm text-slate-700">{p.cost_classification === "CONSULTANT" ? "Consultant" : "Overhead"}</td>
                <td className="py-3 px-4 text-sm text-slate-700">{p.start_date || "—"}</td>
                <td className="py-3 px-4 text-sm text-slate-700">{p.end_date || "—"}</td>
                <td className="py-3 px-4"><ActiveBadge active={p.active} testId={`person-status-${p.id}`} /></td>
                <td className="py-3 px-4 text-right">
                  <div className="flex justify-end gap-2">
                    {p.engagement_type === "EMPLOYEE" && (
                      <Button variant="outline" size="sm" onClick={() => setTermsFor(p)} data-testid={`terms-person-${p.id}`}>Terms</Button>
                    )}
                    {isAdmin && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => openCorrection(p)} data-testid={`correct-engagement-${p.id}`}>
                          Correct engagement
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-person-${p.id}`}>Edit</Button>
                        <Button variant="outline" size="sm" onClick={() => toggleActive(p)} data-testid={`toggle-person-${p.id}`}>
                          {p.active ? "Deactivate" : "Activate"}
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visiblePeople.length === 0 && (
          people.length === 0 ? (
            <EmptyState title="No people yet" description="Add employees and subcontractors." testId="people-empty" />
          ) : (
            <EmptyState title="No matches" description="No people match the current search and filters." testId="people-no-matches" />
          )
        )}
      </div>

      <EmploymentTermsPanel person={termsFor} open={!!termsFor} onClose={() => setTermsFor(null)} />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="person-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit person" : "New person"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="person-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Company</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="person-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Engagement type</Label>
                <Select
                  value={form.engagement_type}
                  onValueChange={(v) =>
                    setForm({ ...form, engagement_type: v, cost_classification: v === "SUBCONTRACTOR" ? "CONSULTANT" : form.cost_classification })
                  }
                >
                  <SelectTrigger data-testid="person-engagement-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EMPLOYEE">Employee</SelectItem>
                    <SelectItem value="SUBCONTRACTOR">Subcontractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Cost classification</Label>
                <Select
                  value={form.cost_classification}
                  onValueChange={(v) => setForm({ ...form, cost_classification: v })}
                  disabled={form.engagement_type === "SUBCONTRACTOR"}
                >
                  <SelectTrigger data-testid="person-classification-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CONSULTANT">Consultant</SelectItem>
                    <SelectItem value="OVERHEAD">Overhead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="person-start-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label>End date (optional — empty = ongoing)</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="person-end-date-input" />
              </div>
            </div>
            {form.engagement_type === "EMPLOYEE" && (
              <div className="space-y-1.5">
                <Label>Employment percentage</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={form.employment_percentage}
                  disabled={!!editing && editHasTerms}
                  onChange={(e) => setForm({ ...form, employment_percentage: e.target.value })}
                  data-testid="person-employment-percentage-input"
                />
                {editing && editHasTerms ? (
                  <p className="text-xs text-slate-500">
                    Managed via effective-dated Employment Terms —{" "}
                    <button
                      type="button"
                      className="text-[#0052CC] font-semibold hover:underline"
                      onClick={() => {
                        setDialogOpen(false);
                        setTermsFor(editing);
                      }}
                      data-testid="person-manage-terms-link"
                    >
                      open Terms
                    </button>
                  </p>
                ) : (
                  <p className="text-xs text-slate-500">
                    Creates the initial effective-dated Employment Terms (from start date, ongoing). Salary and pension can be added later — they are not required for utilization.
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="person-cancel-button">Cancel</Button>
            <Button onClick={save} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="person-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="saved-filter-save-dialog">
          <DialogHeader>
            <DialogTitle>Save current filters</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Filter name</Label>
            <Input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="e.g. Örebro"
              data-testid="saved-filter-name-input"
              onKeyDown={(e) => e.key === "Enter" && saveCurrentFilter()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)} data-testid="saved-filter-save-cancel">Cancel</Button>
            <Button onClick={saveCurrentFilter} disabled={!saveName.trim()} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="saved-filter-save-confirm">Save filter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameTarget} onOpenChange={(v) => !v && setRenameTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="saved-filter-rename-dialog">
          <DialogHeader>
            <DialogTitle>Rename saved filter</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Filter name</Label>
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              data-testid="saved-filter-rename-input"
              onKeyDown={(e) => e.key === "Enter" && renameSavedFilter()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)} data-testid="saved-filter-rename-cancel">Cancel</Button>
            <Button onClick={renameSavedFilter} disabled={!renameValue.trim()} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="saved-filter-rename-confirm">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!correctionFor} onOpenChange={(v) => !v && setCorrectionFor(null)}>
        <DialogContent data-testid="correct-engagement-dialog">
          <DialogHeader>
            <DialogTitle>Correct initial engagement</DialogTitle>
          </DialogHeader>
          {correctionFor && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-slate-600" data-testid="correct-engagement-explainer">
                Data correction for <span className="font-semibold">{correctionFor.name}</span> — the initial engagement was entered incorrectly.
                {correctionFor.engagement_type === "SUBCONTRACTOR"
                  ? " The person becomes an Employee from the original start date; no historical subcontractor period is created."
                  : " The person becomes a Subcontractor from the original start date; the initial Employment Terms record is removed and retained in the audit trail."}
              </p>
              {correctionFor.engagement_type === "SUBCONTRACTOR" && (
                <>
                  <div className="space-y-1.5">
                    <Label>Cost classification</Label>
                    <Select
                      value={correctionForm.cost_classification}
                      onValueChange={(v) => setCorrectionForm({ ...correctionForm, cost_classification: v })}
                    >
                      <SelectTrigger data-testid="correct-classification-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CONSULTANT">Consultant</SelectItem>
                        <SelectItem value="OVERHEAD">Overhead</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label>Employment %</Label>
                      <Input type="number" min="1" max="100" value={correctionForm.employment_percentage}
                        onChange={(e) => setCorrectionForm({ ...correctionForm, employment_percentage: e.target.value })}
                        data-testid="correct-employment-pct-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>FTE monthly salary</Label>
                      <Input type="number" min="0" value={correctionForm.fte_monthly_salary}
                        onChange={(e) => setCorrectionForm({ ...correctionForm, fte_monthly_salary: e.target.value })}
                        data-testid="correct-salary-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Pension</Label>
                      <Input type="number" min="0" value={correctionForm.pension_amount}
                        onChange={(e) => setCorrectionForm({ ...correctionForm, pension_amount: e.target.value })}
                        data-testid="correct-pension-input" />
                    </div>
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <Label>Comment (optional — stored in the audit trail)</Label>
                <Input value={correctionForm.comment}
                  onChange={(e) => setCorrectionForm({ ...correctionForm, comment: e.target.value })}
                  data-testid="correct-comment-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectionFor(null)} data-testid="correct-engagement-cancel">Cancel</Button>
            <Button
              onClick={submitCorrection}
              disabled={
                correctionBusy ||
                (correctionFor?.engagement_type === "SUBCONTRACTOR" &&
                  (!correctionForm.employment_percentage || correctionForm.fte_monthly_salary === "" || correctionForm.pension_amount === ""))
              }
              className="bg-[#0052CC] hover:bg-[#0047B3]"
              data-testid="correct-engagement-confirm"
            >
              {correctionFor?.engagement_type === "SUBCONTRACTOR" ? "Correct to Employee" : "Correct to Subcontractor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
